import ReportContent from '@app/components/reports/components/report-content';
import { HighestEmissionsItemsCard } from '@app/components/reports/components/report-highest-emissions-items';
import { EmissionsCategoryEnum } from '@shared/api/types';
import { itemsByRatingChartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { RatingToTextEnum } from '@shared/lib/utils';
import { useCCFReportResult } from '../../ccf-report-result-context';

export default function FoodAndDrinkItems() {
  const {
    downloadMode,
    result: { foodAndDrink },
  } = useCCFReportResult();
  const data = {
    highest_emissions_items: foodAndDrink?.highestEmissionFoods,
    items_by_rating: foodAndDrink?.itemsByRating,
    items_assessed: foodAndDrink?.itemsByRating.reduce(
      (acc, category) => acc + category.numberOfItems,
      0
    ),
  };

  const chartData = data.items_by_rating?.map((category) => {
    return {
      category: category.rating,
      items: category.numberOfItems,
      fill: `var(--rating-${category.rating.toLowerCase()})`,
    };
  });

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          My Emissions uses an A - E grading to rate food and drinks items based
          on their carbon intensity, that amount of CO₂e emitted per kilogram of
          food.
        </Text>
        <Text variant="h4">Items by carbon rating</Text>
        <div className="flex items-center">
          <SimplePieChart
            dataKey="items"
            nameKey="category"
            chartConfig={itemsByRatingChartConfig}
            chartData={chartData?.reverse()}
            innerRadius={60}
            centerLabel={{
              value: data.items_assessed!,
              label: 'Items assessed',
            }}
            className="size-72"
          />

          <div className="flex h-full flex-col gap-y-3">
            {(
              ['A', 'B', 'C', 'D', 'E'] as Array<keyof typeof RatingToTextEnum>
            ).map((rating, idx) => {
              return (
                <div key={idx} className="flex items-center gap-4">
                  <RatingCloud
                    rating={rating as EmissionsCategoryEnum}
                    format={downloadMode ? 'png' : 'svg'}
                  />
                  <Text variant="reportBody">{RatingToTextEnum[rating]}</Text>
                </div>
              );
            })}
          </div>
        </div>
      </>
      <>
        <Text variant="h3">Items by highest total emissions</Text>
        <div className="space-y-3">
          <div className="grid grid-cols-11 gap-x-4">
            <Text className="col-span-3">Food or drink item</Text>
            <Text className="col-span-2 justify-self-center">Rating</Text>
            <span className="col-span-2 text-center">
              <Text>Total purchased</Text> <Unit variant="kg" />
            </span>
            <span className="col-span-2 text-center">
              <Text>Total emissions</Text>
              <Unit variant="tCO2e" />
            </span>
            <Text className="col-span-2 text-center">% of emissions</Text>
          </div>
          {data.highest_emissions_items?.map((item, idx) => {
            return (
              idx < 6 && (
                <HighestEmissionsItemsCard
                  key={idx}
                  item={item.name}
                  rating={item.rating as EmissionsCategoryEnum}
                  totalPurchased={item.weightKg}
                  totalEmissions={item.emissionsTonnes}
                  pctOfEmissions={item.pctOfCompanyEmissions}
                  downloadMode={downloadMode}
                />
              )
            );
          })}
        </div>
      </>
    </ReportContent>
  );
}
